import {nextTick} from 'vue'
import {createRouter, createWebHistory} from 'vue-router'
import settings from 'src/router/routes/more/settings.js'
import users from 'src/router/routes/management/users.js'
import news from 'src/router/routes/news/news.js'
import registration from 'src/router/routes/public/registration.js'
import timetable from 'src/router/routes/timetable/timetable.js'
import cloud from 'src/router/routes/channels/cloud.js'
import channels from 'src/router/routes/channels/channels.js'
import more from 'src/router/routes/more/more.js'
import management from 'src/router/routes/management/management.js'
import onboarding from 'src/router/routes/management/onboarding.js'
import global from 'src/router/routes/public/global.js'
import notificationCentre from 'src/router/routes/channels/notificationCentre.js'
import login from 'src/router/routes/public/auth.js'
import monitor from 'src/router/routes/public/monitor.js'
import classbookOverview from 'src/router/routes/classbook/classbook.js'
import sickNotes from 'src/router/routes/sickNotes/sickNotes.js'
import calendar from 'src/router/routes/channels/calendar.js'
import loading from 'src/router/routes/public/loading.js'
import notFound from 'src/router/routes/public/notFound.js'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'

const routesAddFromArray = (routes, newRoutes = []) => {
  return newRoutes.reduce((acc, el) => {
    return [...acc, ...(el.length ? el : [el])]
  }, routes)
}

const routes = routesAddFromArray([], [
  settings,
  users,
  news,
  registration,
  timetable,
  cloud,
  channels,
  more,
  management,
  onboarding,
  global,
  notificationCentre,
  login,
  monitor,
  classbookOverview,
  sickNotes,
  calendar,
  loading,
  notFound  // WARNING: Must be the last route to be defined!
])

let firstTargetRouteAfterAppStart = null

// For those components it's necessary to prevent scroll top after page load
const pagesExcludedFromTopScrolling = ['channel-single-chat']

const extractHashAsSelectorFromUrl = (hash) => {
  // Example URL: #read&gid=5&pid=1
  // Expected return: #read
  const posHash = hash && hash.indexOf('#')
  if (!hash || posHash === -1) return
  const posAmpersand = hash.indexOf('&')
  if (posAmpersand === -1) return CSS.escape(hash)
  return CSS.escape(hash.substring(posHash, posAmpersand))
}

const screenReaderCleanup = (appElement = undefined, targetFocusElement = undefined, pseudoElement = undefined) => {
  targetFocusElement?.removeAttribute('tabindex')
  appElement?.removeAttribute('aria-hidden')
  pseudoElement?.remove()
}

export const setScreenReaderFocus = async (to, from, store) => {
  // We need to ensure that screenreaders always start at the top after a route change.
  // This does not work out of the box because a SR would expect a full page reload, which we are not doing (SPA)
  const isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : GlobalPrototypes.store.state.isScreenReaderActive
  const isRouteChanged = from.name !== to.name
  const isFirstPageLoad = !from.name && !!to.name
  const isDeactivatedForToRoute = to.meta?.preventAutomaticScreenReaderFocus
  const forceAggressiveScreenReaderFocus = to.meta?.forceAggressiveScreenReaderFocus
  const isDeactivatedForFromRoute = from.meta?.preventAutomaticScreenReaderFocus
  const isDeactivatedForTimetableGroup = isDeactivatedForFromRoute && from.meta.group === 'timetable' && from.meta?.group === to.meta?.group
  const routeHasTransition = to.meta?.transitionPoint

  // the first page load is handled by the screen reader itself
  if (!isRouteChanged || isFirstPageLoad || !isScreenReaderActive || isDeactivatedForToRoute || isDeactivatedForTimetableGroup) return

  const isLayoutUnchanged = from.meta?.layout === to.meta?.layout

  const isIOS = store.state.isIOS
  const isAndroid = store.state.isAndroid

  // We attempt to focus the first available button or h1 element. This is either a back-button or page-title
  const selector = isIOS ? 'button, h1' : 'html'
  const appElement = document.getElementById('app')

  if (forceAggressiveScreenReaderFocus) {
    appElement.setAttribute('inert', '')
    appElement.setAttribute('aria-hidden', 'true')
  }

  // add pseudo element to trap focus (needed to overcome some very confusing SR behavior)
  if (isLayoutUnchanged) {
    document.body.insertAdjacentHTML(
      'afterbegin',
      '<div id="pseudoTest" inputmode="none" tabindex="-1" style="position:absolute; opacity:0; height:100%; width:100%; top:1px; left:1px; font-size: 16px;" />'
    )
  }

  // attempt to remove focus from currently active element (activated by SR)
  document.activeElement?.blur()

  // if a route a transition point, we need to wait for the transition to be finished before we can query the
  // target element (default duration of transition is 300ms, we add 100ms to be sure it's finished)
  isIOS && routeHasTransition && await GlobalPrototypes.sleep(400)

  // Hide the app wrapper from SR. This is to avoid that SR sets focus on a nearby, interactive element
  isAndroid && appElement?.setAttribute('aria-hidden', 'true')

  // Wait a moment so the page header is rendered
  !forceAggressiveScreenReaderFocus && isIOS && await GlobalPrototypes.sleep(300)

  // find the first focusable element in the new page, see selector variable
  const targetFocusElement = document.querySelector(selector)

  // if no focusable element is found, we do not attempt to focus anything
  if (!targetFocusElement) {
    screenReaderCleanup(appElement)
    return
  }

  // make the target focus element programmatically focusable
  targetFocusElement.setAttribute('tabindex', '-1')

  // also have a reference to the pseudo element so we can focus and remove it later
  const pseudoElement = document.getElementById('pseudoTest')

  if (forceAggressiveScreenReaderFocus) {
    appElement.removeAttribute('inert')
    appElement.removeAttribute('aria-hidden')
  }

  // for some reason, only when a layout change happened, we must focus the pseudo element first
  isLayoutUnchanged && pseudoElement?.focus()

  // Hide the app wrapper from SR. This is to avoid that SR sets focus on a nearby, interactive element
  await GlobalPrototypes.sleep(1)
  isIOS && appElement?.setAttribute('aria-hidden', 'true')

  await nextTick(() => {
    // focus the target element
    targetFocusElement.focus()
  })

  // Wait a moment to ensure the focus is set before cleaning up
  await GlobalPrototypes.sleep(10)

  // Cleanup: remove tabindex from target element, remove aria-hidden from app wrapper, remove pseudo element
  screenReaderCleanup(appElement, targetFocusElement, pseudoElement)
}

export const setFirstTargetRouteAfterAppStart = (route) => {
  firstTargetRouteAfterAppStart = route
}

export const routeToFirstTargetRouteAfterAppStart = async (router, store) => {
  // No target route, stay where you are
  if (!firstTargetRouteAfterAppStart) return

  // Show logout/refresh modal for invalid user
  const isUserInvalid = store.getters['users/getIsUserInValid']
  if (isUserInvalid) {
    await router.replace(GlobalPrototypes.defaultRoute())
    return
  }

  // Redirect to route that was initially requested by user
  // We add a dummy query parameter to force router to navigate even if the target route is the same as the current one.
  // This ensures middlewares are executed again.
  await router.replace({...firstTargetRouteAfterAppStart, query: {...firstTargetRouteAfterAppStart.query, firstValidRouting: true}})
}

export const createSduiRouter = () => {
  return createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior (to, from, savedPosition) {
      // Make sure to.hash isn't empty. Behind the scenes VueJS uses document.querySelector which fails if
      // the value is for example an empty string.
      if (pagesExcludedFromTopScrolling.includes(to.name) && to.hash) {
        return {
          selector: extractHashAsSelectorFromUrl(to.hash)
        }
      }

      if (to.meta?.preventScrollTop) {
        if (savedPosition) return savedPosition
        return {}
      }

      if (to.meta?.transitionPoint && from.meta?.transitionPoint) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve({ top: 0 })
          }, 300)
        })
      }
      return { top: 0 }
    }
  })
}
